import type { Deal } from '@/types/deals';

const GENERIC_STORE_LABEL = 'your store';
const NURTURE_STORE_TAG = /nurture_store=([^·\n]+)/i;

export type NurtureStoreDirective = 'ok' | 'generic' | { override: string } | null;

export type NurtureStoreResolution = {
  storeName: string;
  dealName: string;
  mode: 'confirmed' | 'override' | 'generic' | 'suspect_generic' | 'empty_generic';
  needsReview: boolean;
  reviewReason: string | null;
};

function normalize(value: string | null | undefined): string {
  return value?.trim() ?? '';
}

export function parseNurtureStoreDirective(notes: string | null | undefined): NurtureStoreDirective {
  const match = notes?.match(NURTURE_STORE_TAG);
  if (!match?.[1]) return null;

  const value = match[1].trim();
  if (!value) return null;
  if (value.toLowerCase() === 'ok') return 'ok';
  if (value.toLowerCase() === 'generic') return 'generic';
  return { override: value };
}

export function upsertNurtureStoreDirective(
  notes: string | null | undefined,
  directive: 'ok' | 'generic' | string
): string {
  const cleaned = normalize(notes).replace(NURTURE_STORE_TAG, '').replace(/\s*·\s*$/g, '').trim();
  const tag =
    directive === 'ok'
      ? 'nurture_store=ok'
      : directive === 'generic'
        ? 'nurture_store=generic'
        : `nurture_store=${directive.trim()}`;
  return cleaned ? `${cleaned} · ${tag}` : tag;
}

function emailDomain(email: string | null | undefined): string {
  const normalized = normalize(email).toLowerCase();
  const [, domain = ''] = normalized.split('@');
  return domain.replace(/^www\./, '');
}

function emailStem(email: string | null | undefined): string {
  const domain = emailDomain(email);
  const stem = domain.split('.')[0] ?? '';
  return stem.replace(/[^a-z0-9]/gi, '').toLowerCase();
}

function normalizedName(value: string | null | undefined): string {
  return normalize(value).toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function isSuspectImportStoreName(
  storeName: string,
  contactName?: string | null,
  email?: string | null
): boolean {
  const name = normalize(storeName);
  if (!name) return true;

  const lower = name.toLowerCase();
  if (lower === 'prospect' || lower === 'test' || lower === 'unknown') return true;
  if (name.length < 3) return true;
  if (/^[a-z]{2}$/i.test(name)) return true;

  if (/^[a-z0-9.-]+\.(com|net|org|co|io|us)$/i.test(name)) return true;

  const contact = normalize(contactName);
  if (contact && contact.toLowerCase() === lower) return true;

  const stem = emailStem(email);
  if (stem.length >= 3) {
    const normalizedStore = normalizedName(name);
    if (normalizedStore === stem || normalizedStore.includes(stem) || stem.includes(normalizedStore)) {
      return true;
    }
  }

  const domain = emailDomain(email);
  if (domain && lower.replace(/\s+/g, '') === domain.replace(/\s+/g, '')) return true;

  if (/^(ca|ny|nj|il|oh|co|vt|ri|ma|pa|md|dc|or|wa|ok|mo|mi)$/i.test(name)) return true;

  return false;
}

export function storeReviewReason(
  deal: Pick<Deal, 'name' | 'prospect_contact_name' | 'prospect_contact_email' | 'source'>
): string | null {
  const name = normalize(deal.name);
  if (!name) return 'Missing store name';

  if (isSuspectImportStoreName(name, deal.prospect_contact_name, deal.prospect_contact_email)) {
    if (/\.(com|net|org|co)\b/i.test(name)) return 'Looks like a website, not a dispensary name';
    if (normalize(deal.prospect_contact_name).toLowerCase() === name.toLowerCase()) {
      return 'Store name matches contact name';
    }
    if (name.toLowerCase() === 'prospect') return 'Generic import placeholder';
    return 'Imported store name looks unreliable';
  }

  return null;
}

export function dealNeedsStoreReview(
  deal: Pick<Deal, 'name' | 'source' | 'prospect_contact_name' | 'prospect_contact_email' | 'nurture_notes'>
): boolean {
  if (deal.source !== 'batch_import') return false;

  const directive = parseNurtureStoreDirective(deal.nurture_notes);
  if (directive === 'ok' || (directive !== null && typeof directive === 'object')) {
    return false;
  }

  return storeReviewReason(deal) !== null;
}

export function resolveNurtureStoreName(
  deal: Pick<Deal, 'name' | 'source' | 'prospect_contact_name' | 'prospect_contact_email' | 'nurture_notes'>
): NurtureStoreResolution {
  const dealName = normalize(deal.name);
  const directive = parseNurtureStoreDirective(deal.nurture_notes);
  const needsReview = dealNeedsStoreReview(deal);
  const reviewReason = needsReview ? storeReviewReason(deal) : null;

  if (directive === 'generic') {
    return {
      storeName: GENERIC_STORE_LABEL,
      dealName,
      mode: 'generic',
      needsReview: false,
      reviewReason: null,
    };
  }

  if (directive !== null && typeof directive === 'object') {
    return {
      storeName: directive.override,
      dealName,
      mode: 'override',
      needsReview: false,
      reviewReason: null,
    };
  }

  if (directive === 'ok') {
    return {
      storeName: dealName || GENERIC_STORE_LABEL,
      dealName,
      mode: 'confirmed',
      needsReview: false,
      reviewReason: null,
    };
  }

  if (!dealName) {
    return {
      storeName: GENERIC_STORE_LABEL,
      dealName,
      mode: 'empty_generic',
      needsReview: deal.source === 'batch_import',
      reviewReason,
    };
  }

  if (deal.source === 'batch_import' && isSuspectImportStoreName(dealName, deal.prospect_contact_name, deal.prospect_contact_email)) {
    return {
      storeName: GENERIC_STORE_LABEL,
      dealName,
      mode: 'suspect_generic',
      needsReview: true,
      reviewReason,
    };
  }

  return {
    storeName: dealName,
    dealName,
    mode: 'confirmed',
    needsReview: false,
    reviewReason: null,
  };
}
